import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, resolveImageUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Pencil, Trash2, Save, X, Calendar, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "../components/ImageUploader";
import InventoryTab, { StatusBadge } from "../components/admin/InventoryTab";
import HalloweenAdminTab from "../theme/halloween/HalloweenAdminTab";

const CATEGORIES = ["flower", "wax", "carts", "edibles"];

const DEFAULT_SMALL_BATCH_PACKAGES = [
  { key: "8th", label: "Eighth (3.5g)", price: 0, min_qty: 1 },
  { key: "qr", label: "Quarter (7g)", price: 0, min_qty: 1 },
  { key: "half", label: "Half (14g)", price: 0, min_qty: 1 },
  { key: "oz", label: "Ounce (28g)", price: 0, min_qty: 1 },
];

const DEFAULT_WHOLESALE_PACKAGES = [
  { key: "qp", label: "QP (4 oz)", price: 0, min_qty: 1 },
  { key: "hp", label: "HP (8 oz)", price: 0, min_qty: 1 },
  { key: "lb", label: "Pound (16 oz)", price: 0, min_qty: 1 },
];

const ADMIN_TABS = ["products", "inventory", "brands", "presets", "orders", "home", "drop", "newsletter", "notifications"];

export default function Admin() {
  const { user, loading, openAuth } = useAuth();
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ADMIN_TABS.includes(t) ? t : "products";
  });

  if (loading) {
    return <div className="pt-32 text-center text-gray-500 font-mono-alt tracking-widest">LOADING...</div>;
  }
  if (!user) {
    // Keep the deep link (e.g. /admin?tab=orders&order=...) — sign in on this page instead of bouncing away
    return (
      <div className="pt-36 pb-24 px-6 text-center" data-testid="admin-signin-required">
        <p className="font-mono-alt text-xs tracking-[0.4em] text-[var(--hk-orange)] mb-4">/ OWNER PANEL</p>
        <h1 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tighter mb-3">Sign in required</h1>
        <p className="text-gray-400 text-sm mb-8 max-w-sm mx-auto">
          Sign in with your admin account to open this page. You'll land right back on the order you tapped.
        </p>
        <button
          data-testid="admin-signin-btn"
          onClick={openAuth}
          className="bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-[var(--hk-orange-hover)] transition-colors"
        >
          Sign in
        </button>
      </div>
    );
  }
  if (user.role !== "owner") {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="pt-24 md:pt-28 px-6 md:px-12 lg:px-24 pb-24" data-testid="admin-page">
      <div className="mb-10">
        <p className="font-mono-alt text-xs tracking-[0.4em] text-[var(--hk-orange)] mb-3">/ OWNER PANEL</p>
        <h1 className="font-display text-5xl md:text-7xl font-bold uppercase tracking-tighter">
          Admin
        </h1>
        <p className="text-gray-400 mt-3">Manage the menu, set the next drop, and view orders. Logged in as {user.email}.</p>
      </div>

      <div className="flex gap-2 border-b border-white/10 mb-10 flex-wrap">
        {[
          { key: "products", label: "Products" },
          { key: "inventory", label: "Inventory" },
          { key: "brands", label: "Brands" },
          { key: "presets", label: "Pricing Presets" },
          { key: "orders", label: "Orders" },
          { key: "home", label: "Home Page" },
          { key: "drop", label: "Next Drop" },
          { key: "newsletter", label: "Subscribers" },
          { key: "notifications", label: "Notifications" },
          { key: "theme", label: "Theme" },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-5 py-3 font-display uppercase tracking-widest text-sm transition-colors border-b-2 -mb-px ${
              tab === t.key
                ? "border-[var(--hk-orange)] text-[var(--hk-orange)]"
                : "border-transparent text-gray-500 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "products" && <ProductsTab />}
      {tab === "inventory" && <InventoryTab />}
      {tab === "brands" && <BrandsTab />}
      {tab === "presets" && <PresetsTab />}
      {tab === "orders" && <OrdersTab />}
      {tab === "home" && <HomeContentTab />}
      {tab === "drop" && <DropTab />}
      {tab === "newsletter" && <NewsletterTab />}
      {tab === "notifications" && <NotificationsTab />}
      {tab === "theme" && <HalloweenAdminTab />}
    </div>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [brands, setBrands] = useState([]);
  const [presets, setPresets] = useState([]);

  const load = async () => {
    setLoading(true);
    const [pRes, bRes, prRes] = await Promise.all([
      api.get("/products"),
      api.get("/brands"),
      api.get("/admin/presets").catch(() => ({ data: [] })),
    ]);
    setProducts(pRes.data || []);
    setBrands(bRes.data || []);
    setPresets(prRes.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setCreating(true);
    setEditingId(null);
    setDraft({
      name: "", category: "flower", brand_id: "", price: 0,
      wholesale_price: 0, wholesale_min_qty: 5,
      small_batch_packages: DEFAULT_SMALL_BATCH_PACKAGES.map((p) => ({ ...p })),
      wholesale_packages: DEFAULT_WHOLESALE_PACKAGES.map((p) => ({ ...p })),
      description: "", image: "", strain: "", featured: false,
    });
  };

  const startEdit = (p) => {
    setCreating(false);
    setEditingId(p.id);
    setDraft({
      ...p,
      brand_id: p.brand_id || "",
      wholesale_price: p.wholesale_price ?? "",
      small_batch_packages: (p.small_batch_packages || []).map((x) => ({ ...x })),
      wholesale_packages: (p.wholesale_packages || []).map((x) => ({ ...x })),
    });
  };

  const cancel = () => {
    setCreating(false);
    setEditingId(null);
    setDraft(null);
  };

  const save = async () => {
    try {
      const sbPackages = (draft.small_batch_packages || [])
        .filter((p) => p.key && p.label)
        .map((p) => ({
          key: String(p.key).trim(),
          label: String(p.label).trim(),
          price: parseFloat(p.price) || 0,
          min_qty: parseInt(p.min_qty) || 1,
        }));
      const wPackages = (draft.wholesale_packages || [])
        .filter((p) => p.key && p.label)
        .map((p) => ({
          key: String(p.key).trim(),
          label: String(p.label).trim(),
          price: parseFloat(p.price) || 0,
          min_qty: parseInt(p.min_qty) || 1,
        }));
      const payload = {
        ...draft,
        brand_id: draft.brand_id || null,
        price: parseFloat(draft.price) || 0,
        wholesale_price: draft.wholesale_price === "" ? null : parseFloat(draft.wholesale_price),
        wholesale_min_qty: parseInt(draft.wholesale_min_qty) || 1,
        small_batch_packages: sbPackages,
        wholesale_packages: wPackages,
      };
      if (creating) {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      } else {
        await api.put(`/admin/products/${editingId}`, payload);
        toast.success("Product updated");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-2xl uppercase tracking-tight">Menu</h2>
        <button
          data-testid="admin-new-product-btn"
          onClick={startCreate}
          className="inline-flex items-center gap-2 bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-wider px-4 py-2 rounded-sm hover:bg-[var(--hk-orange-hover)]"
        >
          <Plus size={14} /> New product
        </button>
      </div>

      {(creating || editingId) && draft && (
        <ProductForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} isNew={creating} brands={brands} presets={presets} />
      )}

      {loading ? (
        <p className="text-gray-500 font-mono-alt tracking-widest">LOADING...</p>
      ) : (
        <div className="border border-white/10">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.3em] text-gray-500">
            <div className="col-span-4">Product</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">Small Batch</div>
            <div className="col-span-2">Wholesale (min)</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {products.map((p) => (
            <div key={p.id} data-testid={`admin-row-${p.id}`} className="grid grid-cols-12 gap-3 items-center px-4 py-3 border-b border-white/5 text-sm">
              <div className="col-span-4 flex items-center gap-3">
                <img src={resolveImageUrl(p.image)} alt="" className="w-10 h-10 object-cover border border-white/10" />
                <div className="min-w-0">
                  <p className="font-display uppercase truncate">{p.name}</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">{p.id}</p>
                </div>
              </div>
              <div className="col-span-2 text-xs uppercase text-gray-300">
                {p.category}
                {p.stock_status && p.stock_status !== "untracked" && (
                  <p className="mt-1 normal-case"><StatusBadge status={p.stock_status} testid={`product-stock-${p.id}`} /></p>
                )}
              </div>
              <div className="col-span-2 text-[var(--hk-orange)] font-display">${p.price?.toFixed(2)}</div>
              <div className="col-span-2 font-display">
                {p.wholesale_price != null ? (
                  <span className="text-[var(--hk-purple-text)]">${p.wholesale_price.toFixed(2)} <span className="text-[10px] text-gray-500">/ {p.wholesale_min_qty}</span></span>
                ) : (
                  <span className="text-gray-600">—</span>
                )}
              </div>
              <div className="col-span-2 flex justify-end gap-2">
                <button data-testid={`admin-edit-${p.id}`} onClick={() => startEdit(p)} className="p-2 border border-white/15 hover:border-white/40">
                  <Pencil size={14} />
                </button>
                <button data-testid={`admin-delete-${p.id}`} onClick={() => remove(p.id)} className="p-2 border border-white/15 hover:border-red-400 text-red-400">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductForm({ draft, setDraft, onSave, onCancel, isNew, brands = [], presets = [] }) {
  const set = (k, v) => setDraft({ ...draft, [k]: v });
  const eligibleBrands = brands.filter((b) => b.category === draft.category);
  const showBrandPicker = draft.category === "carts" || draft.category === "wax";
  const smallBatchPresets = presets.filter((p) => p.kind === "small_batch");
  const wholesalePresets = presets.filter((p) => p.kind === "wholesale");
  return (
    <div className="border border-[var(--hk-purple)]/50 bg-[var(--hk-surface)] p-5 mb-6 grid md:grid-cols-2 gap-4" data-testid="admin-product-form">
      <Input label="Name" value={draft.name} onChange={(v) => set("name", v)} testid="admin-input-name" />
      <div>
        <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Category</label>
        <select value={draft.category} onChange={(e) => set("category", e.target.value)}
                data-testid="admin-input-category"
                className="w-full bg-transparent border-b border-white/20 outline-none py-2 text-white">
          {CATEGORIES.map((c) => <option key={c} value={c} className="bg-black">{c}</option>)}
        </select>
      </div>
      {showBrandPicker && (
        <div className="md:col-span-2">
          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">
            Brand {eligibleBrands.length === 0 && <span className="text-yellow-400">— add a brand first under the Brands tab</span>}
          </label>
          <select
            value={draft.brand_id || ""}
            onChange={(e) => set("brand_id", e.target.value)}
            data-testid="admin-input-brand"
            className="w-full bg-transparent border-b border-white/20 outline-none py-2 text-white"
          >
            <option value="" className="bg-black">— Unbranded —</option>
            {eligibleBrands.map((b) => (
              <option key={b.id} value={b.id} className="bg-black">{b.name}</option>
            ))}
          </select>
        </div>
      )}
      <div className="md:col-span-2">
        <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Product image</label>
        <ImageUploader value={draft.image} onChange={(v) => set("image", v)} testId="admin-image-uploader" />
      </div>
      <Input label="Strain" value={draft.strain || ""} onChange={(v) => set("strain", v)} testid="admin-input-strain" />
      <Input label="Small batch base price (USD, fallback when no packages)" type="number" value={draft.price} onChange={(v) => set("price", v)} testid="admin-input-price" />
      <Input label="Wholesale base price (USD, blank = none)" type="number" value={draft.wholesale_price} onChange={(v) => set("wholesale_price", v)} testid="admin-input-wholesale-price" />
      <Input label="Wholesale min qty (when no package selected)" type="number" value={draft.wholesale_min_qty} onChange={(v) => set("wholesale_min_qty", v)} testid="admin-input-min-qty" />
      <div className="md:col-span-2">
        <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Description</label>
        <textarea value={draft.description} onChange={(e) => set("description", e.target.value)}
                  data-testid="admin-input-description"
                  rows={2}
                  className="w-full bg-transparent border border-white/15 outline-none p-2 text-white"/>
      </div>

      <div className="md:col-span-2">
        <PackageEditor
          title="Small batch sizes (retail)"
          presets={DEFAULT_SMALL_BATCH_PACKAGES}
          savedPresets={smallBatchPresets}
          packages={draft.small_batch_packages || []}
          onChange={(v) => set("small_batch_packages", v)}
          showMinQty={false}
          testIdPrefix="admin-sb"
        />
      </div>
      <div className="md:col-span-2">
        <PackageEditor
          title="Wholesale sizes"
          presets={DEFAULT_WHOLESALE_PACKAGES}
          savedPresets={wholesalePresets}
          packages={draft.wholesale_packages || []}
          onChange={(v) => set("wholesale_packages", v)}
          showMinQty={true}
          testIdPrefix="admin-wh"
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" data-testid="admin-input-featured" checked={!!draft.featured}
               onChange={(e) => set("featured", e.target.checked)} />
        <span className="text-gray-300 uppercase text-xs tracking-widest">Featured on home</span>
      </label>
      <div className="md:col-span-2 flex justify-end gap-2 pt-2">
        <button onClick={onCancel} data-testid="admin-cancel-btn" className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-sm uppercase tracking-widest">
          <X size={14} /> Cancel
        </button>
        <button onClick={onSave} data-testid="admin-save-btn" className="inline-flex items-center gap-2 bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-wider px-5 py-2 rounded-sm hover:bg-[var(--hk-orange-hover)]">
          <Save size={14} /> {isNew ? "Create" : "Save"}
        </button>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-white/20 focus:border-[var(--hk-purple)] outline-none py-2 text-white"
      />
    </div>
  );
}

function orderCardBorder(isDelivered, isCancelled) {
  if (isDelivered) return "border-green-500/40";
  if (isCancelled) return "border-red-500/40";
  return "border-white/10";
}

function orderStatusChip(isDelivered, isCancelled) {
  if (isDelivered) return "bg-green-500/20 text-green-300";
  if (isCancelled) return "bg-red-500/20 text-red-300";
  return "bg-[var(--hk-orange)]/20 text-[var(--hk-orange)]";
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("active"); // "active" | "history"
  const [editing, setEditing] = useState(null); // { orderId, idx, val }
  const [highlightId] = useState(() => new URLSearchParams(window.location.search).get("order"));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data || []);
      if (highlightId) {
        const target = (data || []).find((o) => o.id === highlightId);
        if (target && ["delivered", "cancelled"].includes(target.status)) setView("history");
        setTimeout(() => {
          document.getElementById(`order-card-${highlightId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 300);
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const shortageMessage = (d) =>
    "INSUFFICIENT INVENTORY\n\n" +
    (d.shortages || [])
      .map((s) => `${s.name}\n  Available: ${s.available_display} · Requested: ${s.requested_display} · Short: ${s.short_display}`)
      .join("\n");

  const updateStatus = async (id, status, override = false) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status, override_inventory: override });
      toast.success(`Order ${status}`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.shortages) {
        if (window.confirm(`${shortageMessage(d)}\n\nOverride and deduct anyway? Stock may go negative and the override is logged.`)) {
          return updateStatus(id, status, true);
        }
      } else {
        toast.error(typeof d === "string" ? d : "Failed");
      }
    }
  };

  const saveItemEdit = async (orderId, idx, quantity, override = false) => {
    try {
      await api.put(`/admin/orders/${orderId}/items`, {
        items: [{ index: idx, quantity }],
        override_inventory: override,
      });
      toast.success("Order updated — inventory adjusted by the difference");
      setEditing(null);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.shortages) {
        if (window.confirm(`${shortageMessage(d)}\n\nOverride and deduct anyway?`)) {
          return saveItemEdit(orderId, idx, quantity, true);
        }
      } else {
        toast.error(typeof d === "string" ? d : "Failed");
      }
    }
  };

  if (loading) return <p className="text-gray-500 font-mono-alt tracking-widest">LOADING...</p>;

  const ACTIVE_STATUSES = ["pending", "confirmed", "out_for_delivery"];
  const HISTORY_STATUSES = ["delivered", "cancelled"];
  const filtered = orders.filter((o) =>
    view === "active" ? ACTIVE_STATUSES.includes(o.status) : HISTORY_STATUSES.includes(o.status)
  );
  const counts = {
    active: orders.filter((o) => ACTIVE_STATUSES.includes(o.status)).length,
    history: orders.filter((o) => HISTORY_STATUSES.includes(o.status)).length,
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <SubTab active={view === "active"} onClick={() => setView("active")} count={counts.active}
                label="Active" testid="orders-view-active" />
        <SubTab active={view === "history"} onClick={() => setView("history")} count={counts.history}
                label="Order History" testid="orders-view-history" />
      </div>

      {filtered.length === 0 ? (
        <p className="text-gray-500">
          {view === "active" ? "No active orders." : "No completed or cancelled orders yet."}
        </p>
      ) : (
        <div className="space-y-4">
          {filtered.map((o) => {
            const isDelivered = o.status === "delivered";
            const isCancelled = o.status === "cancelled";
            const isHistory = isDelivered || isCancelled;
            return (
              <div
                key={o.id}
                data-testid={`admin-order-${o.id}`}
                id={`order-card-${o.id}`}
                className={`border p-5 bg-black/40 ${orderCardBorder(isDelivered, isCancelled)} ${o.id === highlightId ? "ring-2 ring-[var(--hk-orange)]" : ""}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    {isDelivered && <CheckCircle2 className="text-green-400" size={22} data-testid={`order-${o.id}-completed-icon`} />}
                    {isCancelled && <XCircle className="text-red-400" size={22} data-testid={`order-${o.id}-cancelled-icon`} />}
                    <div>
                      <p className="font-display text-xl uppercase tracking-tight">
                        #{o.order_number}
                        <span className={`text-xs ml-2 px-2 py-0.5 ${orderStatusChip(isDelivered, isCancelled)}`}>{o.status.replace(/_/g, " ")}</span>
                        {o.inventory_state === "deducted" && (
                          <span className="text-[10px] ml-2 px-2 py-0.5 bg-[var(--hk-purple)]/20 text-[var(--hk-purple-text)] uppercase tracking-widest" data-testid={`order-${o.id}-inv-deducted`}>
                            stock deducted
                          </span>
                        )}
                        {o.inventory_state === "restored" && (
                          <span className="text-[10px] ml-2 px-2 py-0.5 bg-white/10 text-gray-300 uppercase tracking-widest" data-testid={`order-${o.id}-inv-restored`}>
                            stock restored
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 uppercase tracking-widest mt-1">
                        {new Date(o.created_at).toLocaleString()} · {o.user_email}
                      </p>
                    </div>
                  </div>
                  <span className="font-display text-2xl text-[var(--hk-orange)]">${o.total?.toFixed(2)}</span>
                </div>
                <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                  <Info label="Customer" value={`${o.delivery?.full_name} · ${o.delivery?.phone}`} />
                  <Info label="Address" value={`${o.delivery?.address}, ${o.delivery?.city}, ${o.delivery?.state}`} />
                  <Info label="Heard via" value={o.how_heard} />
                </div>
                <div className="space-y-1 text-xs text-gray-400 mb-4">
                  {o.items?.map((it, idx) => (
                    <div key={idx} className="flex items-center gap-2 flex-wrap">
                      <span>
                        • {it.name}
                        {it.package_label ? ` — ${it.package_label}` : ""}
                        {" "}({it.batch_mode}) ×
                      </span>
                      {editing?.orderId === o.id && editing?.idx === idx ? (
                        <span className="inline-flex items-center gap-1.5">
                          <input
                            type="number" min="0"
                            value={editing.val}
                            onChange={(e) => setEditing({ ...editing, val: e.target.value })}
                            data-testid={`order-${o.id}-item-${idx}-qty-input`}
                            className="w-16 bg-transparent border border-[var(--hk-orange)] outline-none px-2 py-0.5 text-white text-xs"
                          />
                          <button
                            data-testid={`order-${o.id}-item-${idx}-qty-save`}
                            onClick={() => saveItemEdit(o.id, idx, Math.max(0, parseInt(editing.val) || 0))}
                            className="text-green-300 border border-green-500/40 px-2 py-0.5 uppercase tracking-widest text-[10px]"
                          >
                            Save
                          </button>
                          <button onClick={() => setEditing(null)} className="text-gray-500 border border-white/15 px-2 py-0.5 uppercase tracking-widest text-[10px]">
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          {it.quantity} = ${it.line_total?.toFixed(2)}
                          {!isHistory && (
                            <button
                              data-testid={`order-${o.id}-item-${idx}-edit`}
                              onClick={() => setEditing({ orderId: o.id, idx, val: it.quantity })}
                              title="Edit quantity"
                              className="text-gray-600 hover:text-[var(--hk-orange)]"
                            >
                              <Pencil size={11} />
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                {!isHistory && (
                  <>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <button
                        data-testid={`order-${o.id}-complete`}
                        onClick={() => updateStatus(o.id, "delivered")}
                        className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/40 text-green-300 hover:bg-green-500/20 px-4 py-2 text-xs uppercase tracking-widest font-display font-bold"
                      >
                        <CheckCircle2 size={14} /> Complete order
                      </button>
                      <button
                        data-testid={`order-${o.id}-cancel`}
                        onClick={() => {
                          if (window.confirm("Cancel this order?")) updateStatus(o.id, "cancelled");
                        }}
                        className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/40 text-red-300 hover:bg-red-500/20 px-4 py-2 text-xs uppercase tracking-widest font-display font-bold"
                      >
                        <XCircle size={14} /> Cancel order
                      </button>
                    </div>
                    <details className="text-xs">
                      <summary className="cursor-pointer text-gray-500 uppercase tracking-widest hover:text-white">
                        More statuses
                      </summary>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {["pending", "confirmed", "out_for_delivery"].map((s) => (
                          <button
                            key={s}
                            data-testid={`admin-status-${o.id}-${s}`}
                            onClick={() => updateStatus(o.id, s)}
                            className={`text-xs uppercase tracking-widest px-3 py-1.5 border ${
                              o.status === s ? "border-[var(--hk-orange)] text-[var(--hk-orange)]" : "border-white/15 text-gray-400 hover:text-white"
                            }`}
                          >
                            {s.replace(/_/g, " ")}
                          </button>
                        ))}
                      </div>
                    </details>
                  </>
                )}
                {isHistory && (
                  <div className="space-y-3">
                    {isDelivered && <ReturnPanel order={o} onDone={load} />}
                    <button
                      data-testid={`order-${o.id}-reopen`}
                      onClick={() => updateStatus(o.id, "pending")}
                      className="text-[10px] uppercase tracking-widest text-gray-500 hover:text-white underline"
                    >
                      Reopen
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReturnPanel({ order, onDone }) {
  const [open, setOpen] = useState(false);
  const [qtys, setQtys] = useState({});
  const deductions = order.inventory_deductions || [];
  const returned = order.inventory_returned || {};
  const remaining = deductions
    .map((d) => ({ ...d, remaining: Math.max(0, d.qty - (returned[d.product_id] || 0)) }))
    .filter((d) => d.remaining > 0);

  if (order.return_processed) {
    return (
      <p className="text-[10px] uppercase tracking-widest text-gray-500" data-testid={`order-${order.id}-return-done`}>
        Return processed — {order.return_processed.restocked ? "restocked to inventory" : "NOT restocked"}
      </p>
    );
  }
  const canRestock = order.inventory_state === "deducted" && remaining.length > 0;

  const submit = async (restock) => {
    try {
      const items = restock
        ? remaining
            .map((d) => ({ product_id: d.product_id, qty: Number(qtys[d.product_id] ?? d.remaining) }))
            .filter((i) => i.qty > 0)
        : null;
      await api.post(`/admin/orders/${order.id}/return`, { restock, items });
      toast.success(restock ? "Return restocked to inventory" : "Return recorded — nothing restocked");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  return (
    <div className="border border-white/10 p-3">
      {!open ? (
        <button
          data-testid={`order-${order.id}-return-btn`}
          onClick={() => setOpen(true)}
          className="text-xs uppercase tracking-widest text-gray-400 hover:text-white"
        >
          Process return…
        </button>
      ) : (
        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500">Return to inventory?</p>
          {canRestock ? (
            <div className="space-y-2">
              {remaining.map((d) => (
                <div key={d.product_id} className="flex items-center gap-2 text-xs text-gray-300">
                  <span className="flex-1 truncate">{d.name}</span>
                  <input
                    type="number" min="0" step={d.unit === "oz" ? "0.125" : "1"} max={d.remaining}
                    value={qtys[d.product_id] ?? d.remaining}
                    onChange={(e) => setQtys({ ...qtys, [d.product_id]: e.target.value })}
                    data-testid={`order-${order.id}-return-qty-${d.product_id}`}
                    className="w-24 bg-transparent border border-white/15 outline-none px-2 py-1 text-white"
                  />
                  <span className="text-gray-500 uppercase w-10">{d.unit}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500">No stock was deducted for this order — nothing to restock.</p>
          )}
          <div className="flex gap-2">
            {canRestock && (
              <button
                data-testid={`order-${order.id}-return-yes`}
                onClick={() => submit(true)}
                className="bg-green-500/10 border border-green-500/40 text-green-300 hover:bg-green-500/20 px-3 py-1.5 text-[10px] uppercase tracking-widest font-display font-bold"
              >
                Yes — restock
              </button>
            )}
            <button
              data-testid={`order-${order.id}-return-no`}
              onClick={() => submit(false)}
              className="bg-red-500/10 border border-red-500/40 text-red-300 hover:bg-red-500/20 px-3 py-1.5 text-[10px] uppercase tracking-widest font-display font-bold"
            >
              No — don't restock
            </button>
            <button onClick={() => setOpen(false)} className="text-[10px] uppercase tracking-widest text-gray-500 hover:text-white">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SubTab({ active, onClick, count, label, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-2 text-xs uppercase tracking-widest border ${
        active
          ? "bg-[var(--hk-orange)] text-black border-[var(--hk-orange)]"
          : "border-white/15 text-gray-400 hover:text-white"
      }`}
    >
      {label} <span className="ml-2 opacity-70">{count}</span>
    </button>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500">{label}</p>
      <p className="text-white">{value || "—"}</p>
    </div>
  );
}


function DropTab() {
  const [drop, setDrop] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/drop/next").then((r) => {
      setDrop({
        title: r.data.title || "Next Drop",
        subtitle: r.data.subtitle || "",
        drop_at_local: r.data.drop_at ? toLocalInput(r.data.drop_at) : "",
      });
    });
  }, []);

  const save = async () => {
    if (!drop) return;
    setBusy(true);
    try {
      const payload = {
        title: drop.title.trim() || "Next Drop",
        subtitle: drop.subtitle?.trim() || null,
        drop_at: drop.drop_at_local ? new Date(drop.drop_at_local).toISOString() : null,
      };
      await api.put("/admin/drop/next", payload);
      toast.success("Drop saved");
    } catch (e) {
      toast.error("Save failed");
    }
    setBusy(false);
  };

  if (!drop) return <p className="text-gray-500 font-mono-alt tracking-widest">LOADING...</p>;

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-3 mb-6">
        <Calendar size={18} className="text-[var(--hk-orange)]" />
        <h2 className="font-display text-2xl uppercase tracking-tight">Set the next drop</h2>
      </div>
      <p className="text-gray-400 text-sm mb-8">
        Show a countdown on the home page and let visitors subscribe for drop alerts. Leave date empty to hide the countdown (the email capture stays).
      </p>
      <div className="space-y-5">
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Drop name</label>
          <input
            data-testid="admin-drop-title"
            value={drop.title}
            onChange={(e) => setDrop({ ...drop, title: e.target.value })}
            placeholder="DROP 27"
            className="w-full bg-transparent border-b border-white/20 focus:border-[var(--hk-purple)] outline-none py-2 text-white"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Tagline</label>
          <input
            data-testid="admin-drop-subtitle"
            value={drop.subtitle}
            onChange={(e) => setDrop({ ...drop, subtitle: e.target.value })}
            placeholder="Purple Haze OG returns. Limited run."
            className="w-full bg-transparent border-b border-white/20 focus:border-[var(--hk-purple)] outline-none py-2 text-white"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Drop date & time</label>
          <input
            data-testid="admin-drop-when"
            type="datetime-local"
            value={drop.drop_at_local}
            onChange={(e) => setDrop({ ...drop, drop_at_local: e.target.value })}
            className="w-full bg-transparent border-b border-white/20 focus:border-[var(--hk-purple)] outline-none py-2 text-white"
          />
        </div>
        <button
          onClick={save}
          disabled={busy}
          data-testid="admin-drop-save"
          className="bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-wider px-6 py-3 rounded-sm hover:bg-[var(--hk-orange-hover)] disabled:opacity-60"
        >
          {busy ? "Saving..." : "Save drop"}
        </button>
      </div>
    </div>
  );
}

function toLocalInput(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function NewsletterTab() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/newsletter")
      .then((r) => setSubs(r.data || []))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <p className="text-gray-500 font-mono-alt tracking-widest">LOADING...</p>;
  if (!subs.length) return <p className="text-gray-500">No subscribers yet.</p>;
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-4">
        {subs.length} subscriber{subs.length === 1 ? "" : "s"} · {subs.filter((s) => s.phone).length} with phone
      </p>
      <div className="border border-white/10">
        <div className="grid grid-cols-12 gap-3 px-4 py-2.5 border-b border-white/10 text-[10px] uppercase tracking-[0.3em] text-gray-500">
          <div className="col-span-6">Email</div>
          <div className="col-span-4">Phone</div>
          <div className="col-span-2 text-right">Joined</div>
        </div>
        {subs.map((s) => (
          <div key={s.id} data-testid={`subscriber-${s.id}`} className="grid grid-cols-12 gap-3 items-center px-4 py-2.5 border-b border-white/5 text-sm">
            <span className="col-span-6 text-white truncate">{s.email}</span>
            <span className="col-span-4 text-gray-300 font-mono-alt text-xs tracking-wider">
              {s.phone || <span className="text-gray-600 italic">—</span>}
            </span>
            <span className="col-span-2 text-gray-500 font-mono-alt text-xs uppercase tracking-widest text-right">
              {new Date(s.created_at).toLocaleDateString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

const CHANNEL_FIELDS = [
  { field: "push_enabled", label: "Push" },
  { field: "email_enabled", label: "Email" },
  { field: "sms_enabled", label: "SMS" },
];

function NotificationsTab() {
  const [recipients, setRecipients] = useState([]);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/admin/notify/recipients"),
        api.get("/admin/notifications", { params: { limit: 50 } }),
        api.get("/admin/notify/status"),
      ]);
      setRecipients(r1.data || []);
      setItems(r2.data || []);
      setStatus(r3.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const addRecipient = async () => {
    if (!draft.name.trim()) { toast.error("Enter a name"); return; }
    if (!draft.email.trim() && !draft.phone.trim()) { toast.error("Enter an email or phone"); return; }
    try {
      await api.post("/admin/notify/recipients", {
        name: draft.name.trim(),
        email: draft.email.trim() || null,
        phone: draft.phone.trim() || null,
      });
      setDraft({ name: "", email: "", phone: "" });
      toast.success("Recipient added");
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed to add");
    }
  };

  const toggle = async (r, field) => {
    try {
      await api.patch(`/admin/notify/recipients/${r.id}`, { [field]: !r[field] });
      load();
    } catch { toast.error("Failed"); }
  };

  const removeRecipient = async (r) => {
    if (!window.confirm(`Remove ${r.name} from order notifications?`)) return;
    await api.delete(`/admin/notify/recipients/${r.id}`);
    toast.success("Recipient removed");
    load();
  };

  const sendTest = async (channel) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/notify/test", { channel });
      const sent = (data.results || []).filter((x) => x.status === "sent").length;
      const total = (data.results || []).length;
      if (total === 0) toast.warning(channel === "push" ? "No push devices registered yet" : "No email recipients enabled");
      else toast.success(`Test ${channel}: ${sent}/${total} sent`);
      load();
    } catch { toast.error("Test failed"); }
    setBusy(false);
  };

  const enablePush = async () => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        toast.error("Push not supported in this browser"); return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notification permission was denied"); return; }
      const reg = await navigator.serviceWorker.register("/sw.js");
      const { data } = await api.get("/admin/notify/vapid-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.key),
      });
      await api.post("/admin/push/subscribe", { subscription: sub.toJSON() });
      toast.success("Push notifications enabled on this device");
      load();
    } catch (e) {
      toast.error("Could not enable push on this device");
    }
  };

  if (loading) return <p className="text-gray-500 font-mono-alt tracking-widest">LOADING...</p>;

  return (
    <div data-testid="notifications-tab">
      {/* Channel status */}
      <div className="grid sm:grid-cols-3 gap-3 mb-8">
        <div className={`border p-4 ${status?.email_connected ? "border-green-500/40 bg-green-500/5" : "border-yellow-500/40 bg-yellow-500/5"}`}>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-1">Email</p>
          <p className={`font-display uppercase text-sm ${status?.email_connected ? "text-green-300" : "text-yellow-300"}`} data-testid="notify-email-status">
            {status?.email_connected ? "Connected" : "Not connected"}
          </p>
          {status?.email_connected && <p className="text-[10px] text-gray-500 mt-1 break-all">{status.email_sender}</p>}
        </div>
        <div className="border border-white/15 p-4">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-1">Browser push</p>
          <p className="font-display uppercase text-sm text-white" data-testid="notify-push-status">
            {status?.push_devices || 0} device{status?.push_devices === 1 ? "" : "s"} registered
          </p>
        </div>
        <div className="border border-white/15 p-4">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-1">SMS</p>
          <p className="font-display uppercase text-sm text-gray-400" data-testid="notify-sms-status">
            Disabled — no provider connected
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 mb-8">
        <button data-testid="notify-enable-push-btn" onClick={enablePush}
                className="border border-[var(--hk-purple)] text-[var(--hk-purple-text)] hover:bg-[var(--hk-purple)]/20 px-4 py-2.5 text-xs uppercase tracking-widest font-display font-bold">
          Enable push on this device
        </button>
        <button data-testid="notify-test-email-btn" disabled={busy} onClick={() => sendTest("email")}
                className="border border-white/20 hover:border-[var(--hk-orange)] px-4 py-2.5 text-xs uppercase tracking-widest font-display font-bold disabled:opacity-50">
          Send test email
        </button>
        <button data-testid="notify-test-push-btn" disabled={busy} onClick={() => sendTest("push")}
                className="border border-white/20 hover:border-[var(--hk-orange)] px-4 py-2.5 text-xs uppercase tracking-widest font-display font-bold disabled:opacity-50">
          Send test push
        </button>
      </div>

      {/* Recipients */}
      <div className="mb-10">
        <h3 className="font-display uppercase tracking-tight text-lg mb-3">Notification recipients</h3>
        <div className="border border-white/10">
          {recipients.map((r) => (
            <div key={r.id} data-testid={`notify-recipient-${r.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-white/5">
              <div className="flex-1 min-w-[160px]">
                <p className="font-display uppercase text-sm">{r.name}</p>
                <p className="text-[11px] text-gray-500">{r.email || "no email"}{r.phone ? ` · ${r.phone}` : ""}</p>
              </div>
              <div className="flex gap-2">
                {CHANNEL_FIELDS.map(({ field, label }) => (
                  <button key={field}
                          data-testid={`notify-toggle-${field}-${r.id}`}
                          onClick={() => toggle(r, field)}
                          className={`px-3 py-1 text-[10px] uppercase tracking-widest border ${
                            r[field]
                              ? "border-green-500/50 bg-green-500/15 text-green-300"
                              : "border-white/10 text-gray-600"
                          }`}>
                    {label} {r[field] ? "ON" : "OFF"}
                  </button>
                ))}
              </div>
              <button data-testid={`notify-remove-${r.id}`} onClick={() => removeRecipient(r)}
                      className="text-gray-600 hover:text-red-400" title="Remove recipient">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2 px-4 py-3">
            <input data-testid="notify-add-name" value={draft.name} placeholder="Admin name"
                   onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                   className="flex-1 min-w-[120px] bg-transparent border border-white/15 focus:border-[var(--hk-orange)] outline-none px-3 py-2 text-sm text-white placeholder-gray-600" />
            <input data-testid="notify-add-email" value={draft.email} placeholder="Email address" type="email"
                   onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                   className="flex-1 min-w-[160px] bg-transparent border border-white/15 focus:border-[var(--hk-orange)] outline-none px-3 py-2 text-sm text-white placeholder-gray-600" />
            <input data-testid="notify-add-phone" value={draft.phone} placeholder="Phone (for SMS later)"
                   onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                   className="flex-1 min-w-[140px] bg-transparent border border-white/15 focus:border-[var(--hk-orange)] outline-none px-3 py-2 text-sm text-white placeholder-gray-600" />
            <button data-testid="notify-add-btn" onClick={addRecipient}
                    className="bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-widest text-xs px-4 py-2.5">
              Add
            </button>
          </div>
        </div>
        <p className="text-[10px] text-gray-600 mt-2">
          Each admin can have push, email and SMS turned on or off independently. SMS stays off until an SMS provider is connected.
        </p>
      </div>

      {/* Log */}
      <h3 className="font-display uppercase tracking-tight text-lg mb-3">Sent notifications</h3>
      {items.length === 0 ? (
        <p className="text-gray-500">Nothing sent yet. Use the test buttons above or place an order.</p>
      ) : (
        <div className="space-y-2">
          {items.map((n) => (
            <div key={n.id} data-testid={`notification-${n.id}`} className="border border-white/10 bg-black/40 px-4 py-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="font-mono-alt text-[10px] tracking-[0.3em] uppercase text-[var(--hk-purple-text)]">
                  {n.channel} · {n.kind}{n.order_number ? ` · #${n.order_number}` : ""}
                </span>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${
                  { sent: "bg-green-500/20 text-green-300", failed: "bg-red-500/20 text-red-300" }[n.status] ||
                  "bg-yellow-500/20 text-yellow-300"
                }`}>{n.status}</span>
              </div>
              <p className="text-xs text-gray-400 font-mono-alt mt-1">
                to {n.to}{n.preview ? ` — ${n.preview}` : ""}{n.error ? ` · ${n.error}` : ""}
              </p>
              <p className="text-[10px] text-gray-600 uppercase tracking-widest mt-1">
                {new Date(n.created_at).toLocaleString("en-US", { timeZone: "America/New_York" })} ET
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PackageEditor({ title, presets, savedPresets = [], packages, onChange, showMinQty, testIdPrefix }) {
  const updateRow = (idx, patch) => {
    const next = [...packages];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const removeRow = (idx) => {
    const next = packages.filter((_, i) => i !== idx);
    onChange(next);
  };
  const addRow = () => {
    onChange([...packages, { key: "", label: "", price: 0, min_qty: 1, _uid: crypto.randomUUID() }]);
  };
  const applyDefaults = () => {
    // Merge defaults with existing (don't clobber filled prices)
    const existingByKey = Object.fromEntries(packages.map((p) => [p.key, p]));
    const next = presets.map((p) => existingByKey[p.key] || { ...p });
    onChange(next);
  };
  const applySavedPreset = (presetId) => {
    if (!presetId) return;
    const p = savedPresets.find((sp) => sp.id === presetId);
    if (!p) return;
    onChange((p.packages || []).map((pkg) => ({ ...pkg })));
    toast.success(`Loaded preset: ${p.name}`);
  };

  return (
    <div className="border border-white/10 bg-black/40 p-4 mt-2" data-testid={`${testIdPrefix}-editor`}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <p className="font-display uppercase tracking-tight text-white">{title}</p>
        <div className="flex gap-2 flex-wrap items-center">
          {savedPresets.length > 0 && (
            <select
              defaultValue=""
              onChange={(e) => { applySavedPreset(e.target.value); e.target.value = ""; }}
              data-testid={`${testIdPrefix}-saved-presets`}
              className="text-[10px] uppercase tracking-widest bg-black border border-[var(--hk-purple)]/60 text-[var(--hk-purple-text)] px-3 py-1 hover:border-[var(--hk-purple)]"
            >
              <option value="" className="bg-black">Use saved preset…</option>
              {savedPresets.map((sp) => (
                <option key={sp.id} value={sp.id} className="bg-black">{sp.name}</option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={applyDefaults}
            data-testid={`${testIdPrefix}-load-presets`}
            className="text-[10px] uppercase tracking-widest border border-white/15 px-3 py-1 hover:border-white/40"
          >
            Load defaults
          </button>
          <button
            type="button"
            onClick={addRow}
            data-testid={`${testIdPrefix}-add-row`}
            className="text-[10px] uppercase tracking-widest border border-[var(--hk-orange)]/60 text-[var(--hk-orange)] px-3 py-1 hover:bg-[var(--hk-orange)]/10 inline-flex items-center gap-1"
          >
            <Plus size={12} /> Add size
          </button>
        </div>
      </div>
      {packages.length === 0 ? (
        <p className="text-xs text-gray-500 italic">
          {savedPresets.length > 0
            ? `No sizes yet. Pick a saved preset, click "Load defaults", or "Add size" for custom.`
            : `No sizes yet. Click "Load defaults" for ${presets.map((p) => p.key.toUpperCase()).join(" / ")} or "Add size" for custom.`}
        </p>
      ) : (
        <div className="space-y-2">
          <div className={`grid ${showMinQty ? "grid-cols-12" : "grid-cols-10"} gap-2 text-[10px] uppercase tracking-widest text-gray-500`}>
            <div className="col-span-2">Key</div>
            <div className="col-span-4">Label</div>
            <div className="col-span-3">Price (USD)</div>
            {showMinQty && <div className="col-span-2">Min qty</div>}
            <div className="col-span-1"></div>
          </div>
          {packages.map((p, idx) => (
            <div key={p._uid ?? `row-${idx}`} className={`grid ${showMinQty ? "grid-cols-12" : "grid-cols-10"} gap-2 items-center`} data-testid={`${testIdPrefix}-row-${idx}`}>
              <input
                value={p.key}
                onChange={(e) => updateRow(idx, { key: e.target.value })}
                placeholder="8th"
                data-testid={`${testIdPrefix}-key-${idx}`}
                className="col-span-2 bg-transparent border-b border-white/15 outline-none py-1 text-sm text-white"
              />
              <input
                value={p.label}
                onChange={(e) => updateRow(idx, { label: e.target.value })}
                placeholder="Eighth (3.5g)"
                data-testid={`${testIdPrefix}-label-${idx}`}
                className="col-span-4 bg-transparent border-b border-white/15 outline-none py-1 text-sm text-white"
              />
              <input
                type="number"
                step="0.01"
                value={p.price}
                onChange={(e) => updateRow(idx, { price: e.target.value })}
                placeholder="0.00"
                data-testid={`${testIdPrefix}-price-${idx}`}
                className="col-span-3 bg-transparent border-b border-white/15 outline-none py-1 text-sm text-[var(--hk-orange)]"
              />
              {showMinQty && (
                <input
                  type="number"
                  value={p.min_qty || 1}
                  onChange={(e) => updateRow(idx, { min_qty: e.target.value })}
                  placeholder="1"
                  data-testid={`${testIdPrefix}-min-${idx}`}
                  className="col-span-2 bg-transparent border-b border-white/15 outline-none py-1 text-sm text-white"
                />
              )}
              <button
                type="button"
                onClick={() => removeRow(idx)}
                data-testid={`${testIdPrefix}-remove-${idx}`}
                className="col-span-1 text-gray-500 hover:text-red-400 justify-self-end"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ---------------- Brands Tab ----------------
function BrandsTab() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/brands");
    setBrands(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setCreating(true);
    setEditingId(null);
    setDraft({ name: "", category: "carts", logo: "", description: "" });
  };
  const startEdit = (b) => {
    setCreating(false);
    setEditingId(b.id);
    setDraft({ ...b });
  };
  const cancel = () => {
    setCreating(false);
    setEditingId(null);
    setDraft(null);
  };
  const save = async () => {
    try {
      if (creating) {
        await api.post("/admin/brands", draft);
        toast.success("Brand created");
      } else {
        await api.put(`/admin/brands/${editingId}`, draft);
        toast.success("Brand updated");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this brand? Its products will become unbranded.")) return;
    try {
      await api.delete(`/admin/brands/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-display text-2xl uppercase tracking-tight">Brands</h2>
          <p className="text-xs text-gray-500 mt-1">Only Carts and Wax use brands. Customers see a brand grid first, then products under each.</p>
        </div>
        <button
          data-testid="admin-new-brand-btn"
          onClick={startCreate}
          className="inline-flex items-center gap-2 bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-wider px-4 py-2 rounded-sm hover:bg-[var(--hk-orange-hover)]"
        >
          <Plus size={14} /> New brand
        </button>
      </div>

      {(creating || editingId) && draft && (
        <div className="border border-[var(--hk-purple)]/50 bg-[var(--hk-surface)] p-5 mb-6 grid md:grid-cols-2 gap-4" data-testid="admin-brand-form">
          <Input label="Brand name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} testid="admin-brand-name" />
          <div>
            <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Category</label>
            <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                    data-testid="admin-brand-category"
                    className="w-full bg-transparent border-b border-white/20 outline-none py-2 text-white">
              <option value="carts" className="bg-black">carts</option>
              <option value="wax" className="bg-black">wax</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Logo</label>
            <ImageUploader value={draft.logo} onChange={(v) => setDraft({ ...draft, logo: v })} testId="admin-brand-logo" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Description (optional)</label>
            <textarea value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                      data-testid="admin-brand-description"
                      rows={2}
                      className="w-full bg-transparent border border-white/15 outline-none p-2 text-white"/>
          </div>
          <div className="md:col-span-2 flex justify-end gap-2 pt-2">
            <button onClick={cancel} data-testid="admin-brand-cancel" className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-sm uppercase tracking-widest">
              <X size={14} /> Cancel
            </button>
            <button onClick={save} data-testid="admin-brand-save" className="inline-flex items-center gap-2 bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-wider px-5 py-2 rounded-sm hover:bg-[var(--hk-orange-hover)]">
              <Save size={14} /> {creating ? "Create" : "Save"}
            </button>
          </div>
        </div>
      )}

      {loading && <p className="text-gray-500 font-mono-alt tracking-widest">LOADING...</p>}
      {!loading && brands.length === 0 && (
        <p className="text-gray-500 italic">No brands yet. Add one to start grouping your carts or wax.</p>
      )}
      {!loading && brands.length > 0 && (
        <div className="border border-white/10">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.3em] text-gray-500">
            <div className="col-span-5">Brand</div>
            <div className="col-span-3">Category</div>
            <div className="col-span-2">Logo</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {brands.map((b) => (
            <div key={b.id} data-testid={`admin-brand-row-${b.id}`} className="grid grid-cols-12 gap-3 items-center px-4 py-3 border-b border-white/5 text-sm">
              <div className="col-span-5">
                <p className="font-display uppercase truncate">{b.name}</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">{b.id}</p>
              </div>
              <div className="col-span-3 text-xs uppercase text-gray-300">{b.category}</div>
              <div className="col-span-2">
                {b.logo ? (
                  <img src={resolveImageUrl(b.logo)} alt="" className="w-10 h-10 object-contain border border-white/10 bg-black" />
                ) : (
                  <span className="text-gray-600 text-xs">—</span>
                )}
              </div>
              <div className="col-span-2 flex justify-end gap-2">
                <button onClick={() => startEdit(b)} data-testid={`admin-brand-edit-${b.id}`} className="p-2 border border-white/15 hover:border-white/40">
                  <Pencil size={14} />
                </button>
                <button onClick={() => remove(b.id)} data-testid={`admin-brand-delete-${b.id}`} className="p-2 border border-white/15 hover:border-red-400 text-red-400">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------- Home Page Content Tab ----------------
const HOME_FIELDS = [
  { key: "eyebrow", label: "Hero eyebrow (tiny orange text above headline)" },
  { key: "hero_line1", label: "Headline line 1 (white)" },
  { key: "hero_line2", label: "Headline line 2 (italic purple)" },
  { key: "hero_line3", label: "Headline line 3 (white)" },
  { key: "hero_line4", label: "Headline line 4 (orange)" },
  { key: "hero_subtitle", label: "Hero subtitle (paragraph)", multiline: true },
  { key: "hero_cta", label: "Hero CTA button text" },
  { key: "marquee_text", label: 'Marquee text (use " — " between words)', multiline: false },
  { key: "collections_eyebrow", label: "Collections eyebrow" },
  { key: "collections_title_a", label: 'Collections heading (first part)' },
  { key: "collections_title_b", label: 'Collections heading accent (italic orange)' },
  { key: "collections_desc", label: "Collections description" },
  { key: "featured_eyebrow", label: "Featured eyebrow" },
  { key: "featured_title", label: 'Featured heading' },
  { key: "manifesto_eyebrow", label: "Manifesto eyebrow" },
  { key: "manifesto_text", label: "Manifesto paragraph", multiline: true },
];

function HomeContentTab() {
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/home/content").then((r) => setContent(r.data));
  }, []);

  const save = async () => {
    if (!content) return;
    setBusy(true);
    try {
      await api.put("/admin/home/content", content);
      toast.success("Home page updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  if (!content) return <p className="text-gray-500 font-mono-alt tracking-widest">LOADING...</p>;

  return (
    <div className="max-w-3xl">
      <h2 className="font-display text-2xl uppercase tracking-tight mb-2">Home page text</h2>
      <p className="text-gray-400 text-sm mb-8">
        Edit every line of copy on the home page. Changes show up immediately after save.
      </p>
      <div className="space-y-5">
        {HOME_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">
              {f.label}
            </label>
            {f.multiline ? (
              <textarea
                data-testid={`home-${f.key}`}
                value={content[f.key] || ""}
                onChange={(e) => setContent({ ...content, [f.key]: e.target.value })}
                rows={3}
                className="w-full bg-transparent border border-white/15 focus:border-[var(--hk-purple)] outline-none p-3 text-white"
              />
            ) : (
              <input
                data-testid={`home-${f.key}`}
                value={content[f.key] || ""}
                onChange={(e) => setContent({ ...content, [f.key]: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 focus:border-[var(--hk-purple)] outline-none py-2 text-white"
              />
            )}
          </div>
        ))}
        <div className="flex gap-3 pt-4">
          <button
            onClick={save}
            disabled={busy}
            data-testid="home-save-btn"
            className="bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-wider px-6 py-3 rounded-sm hover:bg-[var(--hk-orange-hover)] disabled:opacity-60"
          >
            {busy ? "Saving..." : "Save changes"}
          </button>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 border border-white/20 hover:border-white/50 text-sm uppercase tracking-widest"
          >
            View home page →
          </a>
        </div>
      </div>
    </div>
  );
}



// ---------------- Pricing Presets Tab ----------------
function PresetsTab() {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/presets");
      setPresets(data || []);
    } catch (e) {
      toast.error("Couldn't load presets");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startCreate = (kind = "small_batch") => {
    setCreating(true);
    setEditingId(null);
    setDraft({
      name: "",
      kind,
      packages: (kind === "small_batch" ? DEFAULT_SMALL_BATCH_PACKAGES : DEFAULT_WHOLESALE_PACKAGES)
        .map((p) => ({ ...p })),
    });
  };

  const startEdit = (p) => {
    setCreating(false);
    setEditingId(p.id);
    setDraft({
      name: p.name,
      kind: p.kind,
      packages: (p.packages || []).map((x) => ({ ...x })),
    });
  };

  const cancel = () => {
    setCreating(false);
    setEditingId(null);
    setDraft(null);
  };

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error("Give the preset a name");
      return;
    }
    const cleanPkgs = (draft.packages || [])
      .filter((p) => p.key && p.label)
      .map((p) => ({
        key: String(p.key).trim(),
        label: String(p.label).trim(),
        price: parseFloat(p.price) || 0,
        min_qty: parseInt(p.min_qty) || 1,
      }));
    if (cleanPkgs.length === 0) {
      toast.error("Add at least one size");
      return;
    }
    const payload = { name: draft.name.trim(), kind: draft.kind, packages: cleanPkgs };
    try {
      if (creating) {
        await api.post("/admin/presets", payload);
        toast.success("Preset created");
      } else {
        await api.put(`/admin/presets/${editingId}`, payload);
        toast.success("Preset updated");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this preset? Products already using it won't be affected.")) return;
    try {
      await api.delete(`/admin/presets/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const smallBatch = presets.filter((p) => p.kind === "small_batch");
  const wholesale = presets.filter((p) => p.kind === "wholesale");

  return (
    <div data-testid="presets-tab">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-4">
        <div>
          <h2 className="font-display text-2xl uppercase tracking-tight">Pricing Presets</h2>
          <p className="text-xs text-gray-500 mt-1 max-w-xl">
            Save reusable price packs and load them with one click when adding a product.
            Editing a preset later won&apos;t change products that already used it.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-new-preset-small"
            onClick={() => startCreate("small_batch")}
            className="inline-flex items-center gap-2 bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-wider px-4 py-2 rounded-sm hover:bg-[var(--hk-orange-hover)] text-xs"
          >
            <Plus size={14} /> New retail preset
          </button>
          <button
            data-testid="admin-new-preset-wholesale"
            onClick={() => startCreate("wholesale")}
            className="inline-flex items-center gap-2 border border-[var(--hk-purple)]/60 text-[var(--hk-purple-text)] hover:bg-[var(--hk-purple)]/10 font-display font-bold uppercase tracking-wider px-4 py-2 rounded-sm text-xs"
          >
            <Plus size={14} /> New wholesale preset
          </button>
        </div>
      </div>

      {(creating || editingId) && draft && (
        <PresetForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} isNew={creating} />
      )}

      {loading && <p className="text-gray-500 font-mono-alt tracking-widest">LOADING...</p>}
      {!loading && presets.length === 0 && (
        <p className="text-gray-500 italic">No presets yet. Create one to speed up future product entry.</p>
      )}
      {!loading && presets.length > 0 && (
        <div className="space-y-8">
          <PresetGroup title="Retail / Small-batch" items={smallBatch} onEdit={startEdit} onRemove={remove} />
          <PresetGroup title="Wholesale" items={wholesale} onEdit={startEdit} onRemove={remove} />
        </div>
      )}
    </div>
  );
}

function PresetGroup({ title, items, onEdit, onRemove }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-3">/ {title}</p>
      <div className="border border-white/10">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.3em] text-gray-500">
          <div className="col-span-4">Name</div>
          <div className="col-span-6">Packages</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {items.map((p) => (
          <div key={p.id} data-testid={`preset-row-${p.id}`} className="grid grid-cols-12 gap-3 items-center px-4 py-3 border-b border-white/5 text-sm">
            <div className="col-span-4">
              <p className="font-display uppercase truncate">{p.name}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">{p.id}</p>
            </div>
            <div className="col-span-6 text-xs text-gray-300 flex flex-wrap gap-1.5">
              {(p.packages || []).map((pk, i) => (
                <span key={`${pk.key || pk.label}-${i}`} className="border border-white/15 px-2 py-0.5 uppercase tracking-wider">
                  {pk.label} · <span className="text-[var(--hk-orange)]">${Number(pk.price).toFixed(2)}</span>
                </span>
              ))}
            </div>
            <div className="col-span-2 flex justify-end gap-2">
              <button onClick={() => onEdit(p)} data-testid={`preset-edit-${p.id}`} className="p-2 border border-white/15 hover:border-white/40">
                <Pencil size={14} />
              </button>
              <button onClick={() => onRemove(p.id)} data-testid={`preset-delete-${p.id}`} className="p-2 border border-white/15 hover:border-red-400 text-red-400">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PresetForm({ draft, setDraft, onSave, onCancel, isNew }) {
  const setPkg = (idx, patch) => {
    const next = [...draft.packages];
    next[idx] = { ...next[idx], ...patch };
    setDraft({ ...draft, packages: next });
  };
  const addRow = () => setDraft({ ...draft, packages: [...draft.packages, { key: "", label: "", price: 0, min_qty: 1, _uid: crypto.randomUUID() }] });
  const removeRow = (idx) => setDraft({ ...draft, packages: draft.packages.filter((_, i) => i !== idx) });
  const isWholesale = draft.kind === "wholesale";
  return (
    <div className="border border-[var(--hk-purple)]/50 bg-[var(--hk-surface)] p-5 mb-8 space-y-4" data-testid="admin-preset-form">
      <div className="grid md:grid-cols-2 gap-4">
        <Input label="Preset name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} testid="admin-preset-name" />
        <div>
          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Type</label>
          <select
            value={draft.kind}
            onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
            data-testid="admin-preset-kind"
            className="w-full bg-transparent border-b border-white/20 outline-none py-2 text-white"
          >
            <option value="small_batch" className="bg-black">small_batch (retail)</option>
            <option value="wholesale" className="bg-black">wholesale</option>
          </select>
        </div>
      </div>
      <div className="border border-white/10 bg-black/40 p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="font-display uppercase tracking-tight text-white">Sizes</p>
          <button
            type="button"
            onClick={addRow}
            data-testid="admin-preset-add-row"
            className="text-[10px] uppercase tracking-widest border border-[var(--hk-orange)]/60 text-[var(--hk-orange)] px-3 py-1 hover:bg-[var(--hk-orange)]/10 inline-flex items-center gap-1"
          >
            <Plus size={12} /> Add size
          </button>
        </div>
        {draft.packages.length === 0 ? (
          <p className="text-xs text-gray-500 italic">No sizes yet. Click &quot;Add size&quot;.</p>
        ) : (
          <div className="space-y-2">
            <div className={`grid ${isWholesale ? "grid-cols-12" : "grid-cols-10"} gap-2 text-[10px] uppercase tracking-widest text-gray-500`}>
              <div className="col-span-2">Key</div>
              <div className="col-span-4">Label</div>
              <div className="col-span-3">Price (USD)</div>
              {isWholesale && <div className="col-span-2">Min qty</div>}
              <div className="col-span-1"></div>
            </div>
            {draft.packages.map((p, idx) => (
              <div key={p._uid ?? `row-${idx}`} className={`grid ${isWholesale ? "grid-cols-12" : "grid-cols-10"} gap-2 items-center`} data-testid={`admin-preset-row-${idx}`}>
                <input
                  value={p.key}
                  onChange={(e) => setPkg(idx, { key: e.target.value })}
                  placeholder="8th"
                  className="col-span-2 bg-transparent border-b border-white/15 outline-none py-1 text-sm text-white"
                />
                <input
                  value={p.label}
                  onChange={(e) => setPkg(idx, { label: e.target.value })}
                  placeholder="Eighth (3.5g)"
                  className="col-span-4 bg-transparent border-b border-white/15 outline-none py-1 text-sm text-white"
                />
                <input
                  type="number"
                  step="0.01"
                  value={p.price}
                  onChange={(e) => setPkg(idx, { price: e.target.value })}
                  placeholder="0.00"
                  className="col-span-3 bg-transparent border-b border-white/15 outline-none py-1 text-sm text-[var(--hk-orange)]"
                />
                {isWholesale && (
                  <input
                    type="number"
                    value={p.min_qty || 1}
                    onChange={(e) => setPkg(idx, { min_qty: e.target.value })}
                    placeholder="1"
                    className="col-span-2 bg-transparent border-b border-white/15 outline-none py-1 text-sm text-white"
                  />
                )}
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  className="col-span-1 text-gray-500 hover:text-red-400 justify-self-end"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} data-testid="admin-preset-cancel" className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-sm uppercase tracking-widest">
          <X size={14} /> Cancel
        </button>
        <button onClick={onSave} data-testid="admin-preset-save" className="inline-flex items-center gap-2 bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-wider px-5 py-2 rounded-sm hover:bg-[var(--hk-orange-hover)]">
          <Save size={14} /> {isNew ? "Create" : "Save"}
        </button>
      </div>
    </div>
  );
}
